/**
 * "Which loan does this submission belong to?" — inferred client-side by
 * matching the submission's people, property and entity against the cached
 * pipeline (GET /underwriting/underwriting_pipeline, which projects `borrowers`,
 * `subjectProperties`, `entities` and `borrowerEntity`, so every signal below is
 * already in hand — no extra request).
 *
 * These are HINTS, not a resolution: the dialog surfaces them as shortcuts above
 * the full searchable list and every one shows why it matched, so a wrong guess
 * is visibly wrong rather than silently applied.
 */

// --- normalizers -----------------------------------------------------------
// Each returns null for "nothing comparable here", so a missing field on either
// side can never match a missing field on the other.

const normEmail = (v) => {
  const s = String(v ?? "")
    .trim()
    .toLowerCase();
  return s.includes("@") ? s : null;
};

// Last 10 digits, so "(555) 123-4567", "5551234567" and "+1 555 123 4567" all
// collapse to the same key.
const normPhone = (v) => {
  const digits = String(v ?? "").replace(/\D/g, "");
  return digits.length >= 10 ? digits.slice(-10) : null;
};

const normName = (first, last) => {
  const s = [first, last]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .replace(/[^a-z\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  // A lone first name matches far too much to be a useful signal.
  return s.includes(" ") ? s : null;
};

// Company suffixes and punctuation are the usual reason "Smith Holdings LLC"
// and "Smith Holdings, L.L.C." don't compare equal.
const normEntity = (v) => {
  const s = String(v ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\b(llc|l l c|inc|incorporated|corp|corporation|co|ltd|lp|llp|trust)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return s.length > 2 ? s : null;
};

const normAddressText = (v) => {
  const s = String(v ?? "")
    .toLowerCase()
    .replace(/,/g, " ")
    .replace(/\b(united states|usa|us)\b\s*$/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return s.length > 5 ? s : null;
};

/**
 * The comparable keys for one address. Two are emitted because the two sides
 * rarely agree on formatting: `fullAddress` is whatever the geocoder or the
 * borrower typed, while street-number + street-name + zip is the part that
 * actually identifies the parcel and survives "Apt 2" / "Ste B" / a missing
 * state.
 */
const addressKeys = (address) => {
  if (!address) return [];
  const keys = [];
  const full = normAddressText(address.fullAddress);
  if (full) keys.push(`full:${full}`);
  const streetNumber = String(address.streetNumber ?? "").trim();
  const streetName = normAddressText(address.streetName);
  const zip = String(address.zip ?? "").trim().slice(0, 5);
  if (streetNumber && streetName) {
    keys.push(`parcel:${streetNumber} ${streetName}${zip ? ` ${zip}` : ""}`);
  }
  return keys;
};

const overlaps = (a, b) => a.some((v) => b.includes(v));

// --- signal extraction -----------------------------------------------------

/**
 * Every identity a submission or a loan carries, flattened into comparable sets.
 *
 * Submissions come in two shapes — migrated legacy docs keep one borrower inline
 * at the top level, newer ones carry a `borrowers` array — so both are read (the
 * same reason submissionBorrowerName does). Loans only ever use the array.
 */
const identitiesOf = (source = {}) => {
  const people = [
    // Top-level borrower fields exist on submissions only; on a loan they're
    // absent and this entry contributes nothing.
    {
      firstName: source.firstName,
      lastName: source.lastName,
      emailAddress: source.emailAddress,
      phone: source.phone ?? source.phoneNumber,
    },
    ...(Array.isArray(source.borrowers) ? source.borrowers : []),
  ];

  const emails = [];
  const phones = [];
  const names = [];
  people.forEach((p) => {
    if (!p) return;
    const email = normEmail(p.emailAddress);
    if (email) emails.push(email);
    const phone = normPhone(p.phone ?? p.phoneNumber);
    if (phone) phones.push(phone);
    const name = normName(p.firstName, p.lastName);
    if (name) names.push(name);
  });

  const entities = [];
  (Array.isArray(source.entities) ? source.entities : []).forEach((e) => {
    const name = normEntity(e?.entityName);
    if (name) entities.push(name);
  });
  const borrowerEntity = normEntity(source.borrowerEntity?.entityName);
  if (borrowerEntity) entities.push(borrowerEntity);

  // Subject properties only — a borrower's home address is not the collateral,
  // and matching one against the other would pair unrelated deals.
  const addresses = [];
  (Array.isArray(source.subjectProperties) ? source.subjectProperties : [])
    .forEach((p) => addresses.push(...addressKeys(p?.address)));

  return { emails, phones, names, entities, addresses };
};

/**
 * What each signal is worth. Email and property are the two that genuinely
 * identify a deal; name and phone are supporting evidence (people share names,
 * and an office line can sit on several borrowers), and entity sits between.
 *
 * Weights only order the list — the reasons are what the user actually judges by.
 */
export const SUGGESTION_SIGNALS = [
  { key: "email", weight: 100, label: "Same borrower email" },
  { key: "address", weight: 80, label: "Matching property" },
  { key: "entity", weight: 50, label: "Same entity" },
  { key: "name", weight: 40, label: "Same borrower name" },
  { key: "phone", weight: 35, label: "Same phone" },
];

const SIGNAL_SETS = {
  email: "emails",
  address: "addresses",
  entity: "entities",
  name: "names",
  phone: "phones",
};

/**
 * Rank pipeline loans against one submission.
 *
 * Returns `[{ loan, score, reasons: [label] }]`, best first, capped at `limit`.
 * Loans with no `loanNumber` are dropped for the same reason the dialog's list
 * drops them: POST /underwriting/assign_to_deal keys on the loan number, so one
 * without it can't be a target.
 */
export const suggestLoansForSubmission = (
  submission,
  pipeline = [],
  { limit = 4 } = {},
) => {
  if (!submission) return [];
  const want = identitiesOf(submission);

  // Nothing comparable on the submission — don't imply a guess we can't make.
  const hasAnySignal = Object.values(want).some((v) => v.length > 0);
  if (!hasAnySignal) return [];

  const ranked = [];
  (Array.isArray(pipeline) ? pipeline : []).forEach((loan) => {
    if (!loan || loan.loanNumber == null) return;
    const have = identitiesOf(loan);

    let score = 0;
    const reasons = [];
    SUGGESTION_SIGNALS.forEach(({ key, weight, label }) => {
      const set = SIGNAL_SETS[key];
      if (overlaps(want[set], have[set])) {
        score += weight;
        reasons.push(label);
      }
    });

    if (score > 0) ranked.push({ loan, score, reasons });
  });

  // Ties break on the newer loan — loanNumber is monotonic, and the pipeline
  // itself arrives sorted by it descending.
  ranked.sort(
    (a, b) => b.score - a.score || Number(b.loan.loanNumber) - Number(a.loan.loanNumber),
  );
  return ranked.slice(0, limit);
};

/**
 * The loan borrower who IS this submission's borrower, or null.
 *
 * Email first, name second — the same precedence submissionBorrowerKey uses, and
 * for the same reason: email is the only field that's both reliably present and
 * stable across two separate submissions by one person.
 *
 * Note `assign_to_deal` does NOT copy the submission's borrower onto the loan —
 * it only pushes the submission body into the deal's `loanApplications` /
 * `creditAuthorizations` array. So this matches against borrowers the loan
 * already had, and returning null is a normal outcome, not an error.
 */
export const matchLoanBorrower = (submission, loan) => {
  const borrowers = Array.isArray(loan?.borrowers) ? loan.borrowers : [];
  if (!borrowers.length) return null;

  const want = identitiesOf(submission);

  const byEmail = borrowers.find((b) => {
    const email = normEmail(b?.emailAddress);
    return email && want.emails.includes(email);
  });
  if (byEmail) return byEmail;

  return (
    borrowers.find((b) => {
      const name = normName(b?.firstName, b?.lastName);
      return name && want.names.includes(name);
    }) || null
  );
};

export default suggestLoansForSubmission;
